import { FuseNavigationItem } from "@fuse/components/navigation";
import { PagePermission } from './permissions.enum';

/**
 * Extension légère du type de navigation pour déclarer les droits d'accès.
 * requiredAnyGroups: l'utilisateur doit posséder AU MOINS un des groupes listés.
 * requiredAllGroups: l'utilisateur doit posséder TOUS les groupes listés (non utilisé pour l'instant, prévu pour évolutions).
 */
export interface PermissionedNavigationItem extends FuseNavigationItem {
    requiredAnyPermissions?: PagePermission[]; // l'utilisateur doit posséder au moins une de ces permissions
    requiredAllPermissions?: PagePermission[]; // l'utilisateur doit posséder toutes ces permissions (réservé évolutions)
    children?: PermissionedNavigationItem[]; // typage récursif
}

// Petit helper (copié localement pour éviter d'importer le service de groupe côté configuration statique)
const hasAnyPermission = (userPermissions: string[] | undefined, required?: string[]): boolean => {
    if (!required || required.length === 0) { return true; }
    if (!userPermissions || userPermissions.length === 0) { return false; }
    return required.some(r => userPermissions.includes(r));
};

const hasAllPermissions = (userPermissions: string[] | undefined, required?: string[]): boolean => {
    if (!required || required.length === 0) { return true; }
    if (!userPermissions || userPermissions.length === 0) { return false; }
    return required.every(r => userPermissions.includes(r));
};

/**
 * Filtre récursivement la navigation selon les groupes.
 * Règles :
 *  - Un item est visible si (requiredAnyGroups OK) ET (requiredAllGroups OK).
 *  - Les enfants sont filtrés; si un conteneur (collapsable / group) n'a plus d'enfant visible => on le retire.
 *  - Les items sans contrainte restent visibles.
 */
export function filterNavigation(base: PermissionedNavigationItem[], userPermissions: string[] | undefined): PermissionedNavigationItem[] {
    return base
        .map(item => ({ ...item })) // shallow clone
        .map(item => {
            if (item.children && item.children.length) {
                item.children = filterNavigation(item.children, userPermissions);
            }
            return item;
        })
        .filter(item => {
            const visible = hasAnyPermission(userPermissions, item.requiredAnyPermissions as string[]) && hasAllPermissions(userPermissions, item.requiredAllPermissions as string[]);
            if (!visible) { return false; }
            // Si c'est un conteneur, il doit avoir des enfants restants (sinon inutile de l'afficher)
            if ((item.type === 'collapsable' || item.type === 'group') && item.children) {
                return item.children.length > 0;
            }
            return true;
        });
}

/**
 * Construit la navigation complète filtrée.
 * @param userGroups groupes de l'utilisateur
 */
export function buildNavigation(userPermissions: string[] | undefined): PermissionedNavigationItem[] {
    return filterNavigation(fullCompactNavigation as PermissionedNavigationItem[], userPermissions);
}

// Navigation « brute » avec métadonnées de permissions (un droit par page / item feuille).
// On suppose que le tableau de permissions utilisateur contient les chaînes définies dans PagePermission.
export const fullCompactNavigation: PermissionedNavigationItem[] = [
    {
        id: 'dashboard-section',
        title: 'Dashboard',
        type: 'collapsable',
        icon: 'heroicons_outline:presentation-chart-bar',
        children: [
            {
                id: 'dashboard-lilate',
                title: 'Dashboard LILATE',
                type: 'basic',
                icon: 'heroicons_outline:presentation-chart-bar',
                link: '/dashboard/lilates',
                requiredAnyPermissions: [PagePermission.DashboardLilate]
            }
        ]
    },
    // 2. Étudiants
    {
        id: 'students-section',
        title: 'Étudiants',
        type: 'collapsable',
        icon: 'heroicons_outline:academic-cap',
        children: [
            {
                id: 'students-list',
                title: 'Liste des étudiants',
                type: 'basic',
                icon: 'heroicons_outline:bars-3',
                link: '/students',
                requiredAnyPermissions: []
            }
        ]
    },
    // 2b. Formations
    {
        id: 'formations-section',
        title: 'Formations',
        type: 'collapsable',
        icon: 'heroicons_outline:book-open',
        link: '/formations',
        children: [
            {
                id: 'formations-list',
                title: 'Liste',
                type: 'basic',
                icon: 'heroicons_outline:bars-3',
                link: '/formations',
                requiredAnyPermissions: []
            }
        ]
    },
    // 3. LILATES
    {
        id: 'lilates-section',
        title: 'LILATES',
        type: 'collapsable',
        icon: 'heroicons_outline:clipboard',
        children: [
            {
                id: 'lilates-list',
                title: 'Liste',
                type: 'basic',
                icon: 'heroicons_outline:bars-3',
                link: '/lilates',
                requiredAnyPermissions: [PagePermission.LilatesListe]
            },
            {
                id: 'lilates-today',
                title: 'Du jour',
                type: 'basic',
                icon: 'heroicons_outline:chat-bubble-bottom-center-text',
                link: '/examination-session-list',
                requiredAnyPermissions: [PagePermission.LilatesDuJour]
            }
        ]
    },
    // 4. Tests LILATES (renommé + déplacé sous LILATES)
    {
        id: 'tests-section', // ID conservé pour éviter de casser d'éventuelles références
        title: 'Tests LILATES',
        type: 'collapsable',
        icon: 'heroicons_outline:cube',
        children: [
            {
                id: 'tests-lilate',
                title: 'Tests LILATE',
                type: 'basic',
                icon: 'heroicons_outline:clipboard-document-list',
                link: '/examination-list',
                requiredAnyPermissions: [PagePermission.TestsLilate]
            },
            {
                id: 'evaluation-slides',
                title: 'Slides d\'évaluation',
                type: 'basic',
                icon: 'heroicons_outline:book-open',
                link: '/exercises-list',
                requiredAnyPermissions: [PagePermission.TestsEvaluationSlides]
            },
            {
                id: 'context-slides',
                title: 'Slides contextuelles',
                type: 'basic',
                icon: 'heroicons_outline:document-text',
                link: '/slide-text-list',
                requiredAnyPermissions: [PagePermission.TestsContextSlides]
            }
        ]
    },
    // 5. E-LATES (renommé + déplacé sous Tests LILATES)
    {
        id: 'elates-section',
        title: 'FlashLevel',
        type: 'collapsable',
        icon: 'heroicons_outline:rectangle-stack',
        link: '/elates',
        children: [
            {
                id: 'elates-link',
                title: 'FlashLevel',
                type: 'basic',
                icon: 'heroicons_outline:rectangle-stack',
                link: '/elates',
                requiredAnyPermissions: [PagePermission.Elates]
            }
        ]
    },
    // 6. Partenaires (extrait indépendant)
    {
        id: 'partners-section',
        title: 'Partenaires',
        type: 'collapsable',
        icon: 'heroicons_outline:users',
        children: [
            {
                id: 'partners-list',
                title: 'Partenaires',
                type: 'basic',
                icon: 'heroicons_outline:users',
                link: '/partners',
                requiredAnyPermissions: [PagePermission.PartnersListe]
            }
        ]
    },
    {
        id: 'entreprises-section',
        title: 'Entreprises',
        type: 'collapsable',
        icon: 'heroicons_outline:building-office-2',
        requiredAnyPermissions: [PagePermission.EntreprisesSection],
        children: [
            {
                id: 'personas-corporates',
                title: 'Entreprises',
                type: 'basic',
                icon: 'heroicons_outline:building-office-2',
                link: '/personas/corporates',
                requiredAnyPermissions: [PagePermission.EntreprisesSection]
            },
            {
                id: 'personas-companies',
                title: 'Comptes',
                type: 'basic',
                icon: 'heroicons_outline:building-office',
                link: '/personas/companies',
                requiredAnyPermissions: [PagePermission.EntreprisesSection]
            },
            {
                id: 'personas-group-formations',
                title: 'Groupes de formations',
                type: 'basic',
                icon: 'heroicons_outline:user-group',
                link: '/personas/group-formations',
                requiredAnyPermissions: [PagePermission.EntreprisesSection]
            }
        ]
    },
    // 8. Référentiels
    {
        id: 'referentials-section',
        title: 'Référentiels',
        type: 'collapsable',
        icon: 'heroicons_outline:folder',
        children: [
            {
                id: 'referentials-business',
                title: 'Secteurs d\'activité',
                type: 'basic',
                icon: 'heroicons_outline:building-office',
                link: '/business-list',
                requiredAnyPermissions: [PagePermission.ReferentialBusiness]
            },
            {
                id: 'referentials-jobs',
                title: 'Métiers',
                type: 'basic',
                icon: 'heroicons_outline:briefcase',
                link: '/job-list',
                requiredAnyPermissions: [PagePermission.ReferentialJobs]
            }
        ]
    },
    // 9. Exports (lien direct)
    {
        id: 'exports',
        title: 'Exports',
        type: 'basic',
        icon: 'heroicons_outline:arrow-down-tray',
        link: '/export',
        requiredAnyPermissions: [PagePermission.ExportMain]
    }
];
